import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { CircularProgress } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CopyIcon } from './icons/CopyIcon';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';
import { Modal } from '../../components/Modal/Modal';
import { getClientSecret, regenerateClientSecret } from '../../api/settingsApi';
import css from './SettingsPage.module.scss';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState<'admin' | 'alerts'>('admin');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await getClientSecret();
      setClientId(data.clientId);
      setClientSecret(data.clientSecret); // Will be empty string from mock
    } catch (error) {
      console.error('Failed to fetch client settings', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setIsLoading(true);
      const data = await regenerateClientSecret();
      setClientId(data.clientId);
      setClientSecret(data.clientSecret);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to regenerate secret', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Optional: Add toast notification here
  };

  if (isLoading && !clientId) {
    return <CircularProgress sx={{ margin: 'auto', display: 'flex', marginTop: '50px' }} />;
  }

  return (
    <section className={css.container}>
      <h1 className={css.header}>Settings</h1>

      <div className={css.tabs}>
        <div
          className={cx(css.tab, activeTab === 'admin' && css.active)}
          onClick={() => setActiveTab('admin')}
        >
          Admin
        </div>
        <div
          className={cx(css.tab, activeTab === 'alerts' && css.active)}
          onClick={() => setActiveTab('alerts')}
        >
          Alerts
        </div>
      </div>

      {activeTab === 'admin' && (
        <div className={css.content}>
          <div className={css.section}>
            <h2 className={css.sectionTitle}>Integration Security</h2>

            <div className={css.fieldGroup}>
              <div className={css.label}>Client Id</div>
              <div className={css.clientInputContainer}>
                <Input
                  value={clientId}
                  onChange={() => { }}
                  width="400px" // Adjust to match design
                  disabled
                />
              </div>
            </div>

            <div className={css.warningBox}>
              <b>Warning:</b>
              <br />
              Regenerating the client secret will immediately invalidate the current one. All
              external systems that use this client must be updated with a new secret.
            </div>

            <div className={css.fieldGroup}>
              <div className={css.label}>New Client Secret</div>
              <div className={css.clientInputContainer}>
                <Input
                  value={clientSecret}
                  onChange={() => { }}
                  width="400px"
                  type={showSecret ? 'text' : 'password'}
                  disabled
                />
                <div className={css.actions}>
                  <div className={css.iconButton} onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </div>
                  <div className={css.iconButton} onClick={() => copyToClipboard(clientSecret)}>
                    <CopyIcon />
                  </div>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    width="120px"
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpen} handleClose={() => setIsModalOpen(false)}>
        <div className={css.modalContent}>
          <div className={css.modalIcon}>
            <HelpOutlineIcon fontSize="inherit" />
          </div>
          <h2 className={css.modalTitle}>Regenerate client secret?</h2>
          <p className={css.modalDescription}>
            Current integrations will stop working until you update them with the new secret.
          </p>
          <div className={css.modalActions}>
            <Button
              onClick={() => setIsModalOpen(false)}
              variant="outlined"
              width="140px"
              color="error" // Re-using error color for visual distinction if allowed, or default
            >
              Stay in this page
            </Button>
            <Button
              onClick={handleRegenerate}
              color="error"
              width="140px"
            >
              Renerate
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default SettingsPage;
